import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MessageStatus,
  MessageType,
  PositionSource,
  useWebSocketContext,
} from "@moonscape/shared";

export function useWebSocketPosition({ exchangeId }: { exchangeId?: string }) {
  const [positionSource, setPositionSource] = useState<PositionSource | null>(null);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const { sendMessageAsync, isConnected } = useWebSocketContext();
  const hasSentInitialMessage = useRef(false);
  const lastExchangeId = useRef<string | undefined>(exchangeId);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const query = useQuery({
    queryKey: exchangeId
      ? [MessageType.POSITION, exchangeId]
      : [MessageType.POSITION],
    queryFn: async () => null,
    enabled: false,
  });

  console.log("🔍 [useWebSocketPosition] Hook state:", {
    exchangeId,
    isConnected,
    queryStatus: query.status,
    queryData: query.data,
    queryError: query.error,
    hasSentInitialMessage: hasSentInitialMessage.current,
  });

  useEffect(() => {
    // Reset flag if exchangeId changes
    if (lastExchangeId.current !== exchangeId) {
      console.log("🔍 [useWebSocketPosition] ExchangeId changed, resetting flag");
      hasSentInitialMessage.current = false;
      lastExchangeId.current = exchangeId;
      setLoadingTimedOut(false);
      // Clear any pending retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }

    console.log("🔍 [useWebSocketPosition] Connection effect:", {
      isConnected,
      exchangeId,
      hasSentInitialMessage: hasSentInitialMessage.current,
      willSendMessage: isConnected && !hasSentInitialMessage.current,
    });

    // Only send message once per exchangeId when connected
    if (isConnected && !hasSentInitialMessage.current) {
      hasSentInitialMessage.current = true;
      const payload = exchangeId ? { exchangeId } : {};
      console.log("📤 [useWebSocketPosition] Sending WS message:", {
        type: MessageType.POSITION,
        payload,
      });
      sendMessageAsync({
        type: MessageType.POSITION,
        payload,
      });

      // If no data arrives within 5 seconds, reset flag to allow retry when connection improves
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        if (!query.data && isConnected) {
          console.log("⚠️ [useWebSocketPosition] No response received, retrying message");
          hasSentInitialMessage.current = false;
        }
      }, 5000);

      // Cap loading state - after 8s with no data, stop showing skeletons
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      loadingTimeoutRef.current = setTimeout(() => {
        if (!query.data) {
          console.log("⚠️ [useWebSocketPosition] Loading timed out, showing empty state");
          setLoadingTimedOut(true);
        }
      }, 8000);
    }

    // Cleanup timeouts on unmount
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [isConnected, exchangeId, sendMessageAsync, query.data]);

  // Reset timeout flag if data eventually arrives
  useEffect(() => {
    if (query.data && loadingTimedOut) {
      setLoadingTimedOut(false);
    }
  }, [query.data, loadingTimedOut]);

  const isLoading = !loadingTimedOut && (
    query.isLoading ||
    !query.data ||
    query?.data?.status === MessageStatus.PROCESSING
  );

  console.log("🔍 [useWebSocketPosition] isLoading calculation:", {
    queryIsLoading: query.isLoading,
    hasQueryData: !!query.data,
    queryDataStatus: query?.data?.status,
    resultIsLoading: isLoading,
  });

  useEffect(() => {
    console.log("🔍 [useWebSocketPosition] Position source effect:", {
      hasQueryData: !!query.data,
      hasResponseMessage: !!query.data?.responseMessage,
      positionSource,
      totalsV2: query.data?.responseMessage?.totalsV2,
    });

    if (!query.data?.responseMessage || positionSource !== null) return;

    const totalsV2 = query.data?.responseMessage.totalsV2 || {};
    const assets = Object.values(totalsV2);

    const hasAPIData = assets.some((asset: any) => asset?.amount > 0);
    const hasTXNSData = assets.some(
      (asset: any) => asset?.calculatedAmount > 0
    );

    console.log("🔍 [useWebSocketPosition] Position source determination:", {
      assetsCount: assets.length,
      hasAPIData,
      hasTXNSData,
      selectedSource: hasAPIData ? "API" : hasTXNSData ? "TXNS" : "API (default)",
    });

    if (hasAPIData) {
      setPositionSource(PositionSource.API);
    } else if (hasTXNSData) {
      setPositionSource(PositionSource.TXNS);
    } else {
      setPositionSource(PositionSource.API);
    }
  }, [query.data, positionSource]);

  const resolvedPositionSource = positionSource ?? PositionSource.API;

  const returnData = {
    ...query,
    data: query?.data?.responseMessage,
    isLoading,
    positionSource: resolvedPositionSource,
    setPositionSource,
    // Add custom error handling for specific cases
    error:
      query.error || (query.data as any)?.error === "tax jurisdiction not set"
        ? new Error("Complete your Tax Settings to see Balances")
        : query.error,
  };

  console.log("🔍 [useWebSocketPosition] Returning:", {
    hasData: !!returnData.data,
    isLoading: returnData.isLoading,
    hasError: !!returnData.error,
    positionSource: returnData.positionSource,
  });

  // Enhanced return with custom error messages
  return returnData;
}
